import { v4 as uuidv4 } from 'uuid'
import type { UseQueryResult } from '@tanstack/react-query'

import type { Address } from '@dhl/components'
import {
  type Piece,
  type PieceLocation,
  type PieceLocationType,
  type PieceTimeIndication,
  type PieceType,
  type Sender,
  sortByMomentIndication,
  sortByReturnedDaysAgo,
  sortByUndeliverableSinceDaysAgo,
} from '@dhl/pieces'
import {
  type Address as ApiAddress,
  type Receiver as ApiReceiver,
  type Sender as ApiSender,
  type IntervalIndication,
  type Location,
  LockersDHL,
  LockersDeBuren,
  LockersSmartmile,
  ParcelStation,
  type MomentIndication,
  type ParcelResponse,
  type PostalCodeValidation,
  type ReceiverParcelSummary,
  type TimeIndication,
  useGetParcels,
} from '@dhl/receiver-parcel-api'
import type { AppError } from '@dhl/receiver-profile-api'

import { profileAddressByAddressSelector, useGetProfile } from '~/profile/model/profile/hooks.js'

// TODO: Move this stuff to the backend
const addressToPieceDomain = (address: ApiAddress | undefined): Address | undefined => {
  if (!address) {
    return undefined
  }
  return {
    countryCode: address.countryCode,
    postalCode: address.postalCode,
    city: address.city,
    street: address.street,
    houseNumber: address.houseNumber,
    houseNumberSuffix: address.houseNumberSuffix,
    suburb: address.city,
    additionalAddressLine: undefined,
    lines: address.lines,
  }
}

const locationToPieceDomain = (location: Location | undefined): PieceLocation | undefined => {
  if (!location) {
    return undefined
  }
  return {
    name: location.name,
    contactName: location.name,
    locationType: location.locationType as PieceLocationType,
    address: addressToPieceDomain(location.address),
    email: undefined,
    profileAddressName: undefined,
    isServicePointOrTerminal: undefined,
    isDHLDeliveryPoint: undefined,
  }
}

const receiverToPieceDomain = (receiver: ApiReceiver | undefined): PieceLocation | undefined => {
  if (!receiver) {
    return undefined
  }
  return {
    name: receiver.name,
    contactName: receiver.name,
    locationType: undefined,
    address: addressToPieceDomain(receiver.address),
    email: receiver.email,
    profileAddressName: undefined,
    isServicePointOrTerminal: undefined,
    isDHLDeliveryPoint: undefined,
  }
}

const senderToPieceDomain = (sender: ApiSender | undefined): Sender | undefined => {
  if (!sender) {
    return undefined
  }
  return {
    name: sender.name,
    address: addressToPieceDomain(sender.address),
  }
}

const timeIndicationToPieceDomain = (indication: TimeIndication | undefined): PieceTimeIndication | undefined => {
  if (!indication) {
    return undefined
  }

  const isIntervalIndication = (ind: TimeIndication): ind is IntervalIndication => 'start' in ind && 'end' in ind
  const isMomentIndication = (ind: TimeIndication): ind is MomentIndication => 'moment' in ind

  if (isIntervalIndication(indication)) {
    return {
      indicationType: 'IntervalIndication',
      start: indication.start,
      end: indication.end,
    }
  }

  if (isMomentIndication(indication)) {
    return {
      indicationType: 'MomentIndication',
      moment: indication.moment,
    }
  }
  return undefined
}

const toPiece =
  (pieceType: PieceType) =>
  (apiPiece: ReceiverParcelSummary): Piece => ({
    orderId: undefined,
    pieceId: apiPiece.parcelId,
    type: pieceType,
    barcode: apiPiece.barcode ?? apiPiece.parcelId,
    destination: locationToPieceDomain(apiPiece.destination),
    receiver: receiverToPieceDomain(apiPiece.receiver),
    sender: senderToPieceDomain(apiPiece.sender),
    timeCreated: undefined,
    receivingTimeIndication: timeIndicationToPieceDomain(apiPiece.receivingTimeIndication),
    status: apiPiece.status,
    category: apiPiece.category,
    intervenable: apiPiece.intervenable,
    intervenableBarcode: null,
    totalPrice: null,
    receivedDaysAgo: apiPiece.receivedDaysAgo,
    returnedDaysAgo: apiPiece.returnedDaysAgo,
    isLockerDelivery: [LockersDHL, LockersDeBuren, LockersSmartmile, ParcelStation].includes(
      `${apiPiece.destination?.servicePointFormat}`
    ),
    // in the API it is `isReturnedToShipper`. But internally we
    // want to handle it as isReturn and later replace in the API
    isReturn: apiPiece.isReturnedToShipper,
    isRegistered: apiPiece.isRegistered,
    printlessCode: undefined,
    identifier: apiPiece.barcode || uuidv4(),
    isClimateNeutralDelivery: apiPiece.isClimateNeutralDelivery,
    isUndeliverable: apiPiece.undeliverableSinceDaysAgo != null,
    undeliverableSinceDaysAgo: apiPiece.undeliverableSinceDaysAgo,
    returnable: apiPiece.returnable,
    hasDeliveryCode: apiPiece.hasDeliveryCode,
    hasLockerCode: apiPiece.hasLockerCode,
    hasServicePointPickupCode: apiPiece.hasServicePointPickupCode,
  })

const getInTransitIncomingPieces = (pieces: Piece[]): Piece[] =>
  pieces.filter(
    (p) => p.type === 'incoming' && p.category !== 'DELIVERED' && !p.isReturn && p.undeliverableSinceDaysAgo == null
  )

const getRecentlyDeliveredIncomingPieces = (pieces: Piece[]): Piece[] =>
  pieces
    .filter(
      (p) =>
        p.type === 'incoming' &&
        p.category === 'DELIVERED' &&
        p.receivedDaysAgo === 0 &&
        !p.isReturn &&
        p.undeliverableSinceDaysAgo == null
    )
    .sort(sortByReturnedDaysAgo)

const getDeliveredIncomingPieces = (pieces: Piece[]): Piece[] =>
  pieces
    .filter(
      (p) => p.type === 'incoming' && p.category === 'DELIVERED' && !p.isReturn && p.undeliverableSinceDaysAgo == null
    )
    .sort(sortByMomentIndication)

const getReturnedIncomingPieces = (pieces: Piece[]): Piece[] =>
  pieces.filter((p) => p.isReturn).sort(sortByReturnedDaysAgo)

const getUndeliverableIncomingPieces = (pieces: Piece[]): Piece[] =>
  pieces.filter((p) => p.isUndeliverable && !p.isReturn).sort(sortByUndeliverableSinceDaysAgo)

type Response = {
  parcels: Piece[]
  postalCodeValidations?: PostalCodeValidation[]
  inTransit: Piece[]
  recentlyDelivered: Piece[]
  delivered: Piece[]
  returned: Piece[]
  undeliverable: Piece[]
}

export const useGetIncomingPieces = (): UseQueryResult<Response, AppError | null> => {
  const { data: profile } = useGetProfile()

  const addProfileAddressName = (pieces: Piece[]): Piece[] =>
    pieces.map((piece) => ({
      ...piece,
      destination: piece.destination
        ? {
            ...piece.destination,
            profileAddressName: profileAddressByAddressSelector(profile, piece.destination?.address)?.name,
          }
        : undefined,
    }))

  const transformResponse = (response: ParcelResponse): Pick<Response, 'parcels' | 'postalCodeValidations'> => ({
    parcels: addProfileAddressName((response?.parcels || []).map(toPiece('incoming'))),
    postalCodeValidations: response.postalCodeValidations,
  })

  return useGetParcels<Response, AppError>(
    {
      tab: 'incoming',
    },
    {
      query: {
        staleTime: 60 * 1000,
        select: (data) => {
          const result = transformResponse(data)
          const { parcels } = result
          const inTransit = getInTransitIncomingPieces(parcels)
          const recentlyDelivered = getRecentlyDeliveredIncomingPieces(parcels)
          const delivered = getDeliveredIncomingPieces(parcels)
          const returned = getReturnedIncomingPieces(parcels)
          const undeliverable = getUndeliverableIncomingPieces(parcels)

          return {
            ...result,
            inTransit,
            recentlyDelivered,
            delivered,
            returned,
            undeliverable,
          }
        },
      },
    }
  )
}
